import { useCallback, useEffect, useState } from 'react'
import { orderRepository } from '../../data/repositories/OrderRepositoryImpl'
import { OrderListItem, OrderStatus } from '../../domain/entities/Order'

export const useOrders = (initialLimit = 10) => {
  const [orders, setOrders] = useState<OrderListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<OrderStatus | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchOrders = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await orderRepository.getOrders({
        search: search || undefined,
        status,
        moqCheck: true,
        page,
        limit: initialLimit,
      })
      setOrders(res.orders)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load orders.')
    } finally {
      setLoading(false)
    }
  }, [search, status, page, initialLimit])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Deliberately does not touch `error` (the page-level fetch-error banner) — the caller's
  // modal displays this error itself, so surfacing it here too would show it twice.
  const updateStatus = async (id: string, nextStatus: OrderStatus, currentVersion: number) => {
    await orderRepository.updateOrderStatus(id, nextStatus, currentVersion)
    await fetchOrders()
  }

  const handleSetSearch = (val: string) => {
    setSearch(val)
    setPage(1)
  }

  const resetFilters = () => {
    setSearch('')
    setStatus(undefined)
    setPage(1)
  }

  return {
    orders,
    loading,
    error,
    search,
    setSearch: handleSetSearch,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    updateStatus,
    resetFilters,
    refresh: fetchOrders,
  }
}
